import type { SceneNode } from "@/document/model/nodes";
import { normalizeStrokeStack, type StrokeAlign } from "@/document/model/styles";
import type { VectorEdge, VectorNetwork, VectorStrokeJoin, VectorVertex } from "@/document/model/vectorNetwork";
import { adaptiveCurveSteps } from "@/document/operations/curveKernels";
import { sampleEdgeBezier } from "@/document/operations/vectorGeometry";
import type { Point } from "@/document/operations/pathGeometry";
import type { DisplayItem } from "../displayList";
/**
 * Stroke tessellation helpers.
 *
 * Production stroke meshes go through `meshKernels` (Wasm when enabled).
 * `strokePolylineToTriangles` / `networkToStrokeTriangles` below are the **TS
 * fallback** implementations registered by `meshKernelsBootstrap` — do not call
 * them from new production paths.
 */
import {
  formatCacheNumber,
  cachedStrokeTriangleChunk,
  maxStrokeChunkCacheEntries,
  setCappedCacheEntry,
  strokeTriangleChunkCacheByKey,
  strokeTriangleChunkPointSize,
  vectorCurveTolerance,
} from "./cache";
import {
  appendNumbers,
  boundsFromVertices,
  paintToDisplayPaint,
  removeDuplicatePoints,
  triangulateEvenOddContours,
} from "./pathNodes";
import { nearbyPointLookupKeys, pointLookupKey, vectorLookup, vertexMap, type VectorLookup } from "./vectorNodes";
import { signedArea } from "@/document/operations/pathGeometry";
import { strokePolylineTriangles } from "./meshKernels";

export function strokeDisplayItems(node: SceneNode, points: Array<{ x: number; y: number }>, closed: boolean): DisplayItem[] {
  return normalizeStrokeStack(node.style).flatMap(stroke => {
    if (stroke.width <= 0) {
      return [];
    }

    const vertices = strokePolylineTriangles(points, stroke.width, closed, stroke.align);
    if (vertices.length === 0) {
      return [];
    }

    return [{
      id: node.id,
      kind: "triangles",
      bounds: boundsFromVertices(vertices),
      vertices,
      fill: paintToDisplayPaint(stroke.paint, node.bounds, "#ffffff"),
    }];
  });
}

export function strokeGeometryCacheKey(width: number, align: StrokeAlign | undefined): string {
  return `${width}:${align ?? "inside"}`;
}

export function ellipsePoints(bounds: { x: number; y: number; width: number; height: number }, segments: number): Array<{ x: number; y: number }> {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const rx = bounds.width / 2;
  const ry = bounds.height / 2;
  return Array.from({ length: segments }, (_, index) => {
    const angle = (index / segments) * Math.PI * 2;
    return {
      x: cx + Math.cos(angle) * rx,
      y: cy + Math.sin(angle) * ry,
    };
  });
}

export function ellipseSegmentCount(bounds: { width: number; height: number }): number {
  const maxRadius = Math.max(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  if (maxRadius <= 0) {
    return 96;
  }

  const tolerance = 0.01;
  const angle = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - tolerance / maxRadius)));
  return Math.min(4096, Math.max(96, Math.ceil((Math.PI * 2) / Math.max(0.001, angle))));
}

/**
 * TS fallback: stroke a vector network into a flat triangle list.
 * @internal Registered via meshKernelsBootstrap — prefer `networkStrokeTriangles`.
 */
export function networkToStrokeTriangles(network: VectorNetwork, strokeWidth: number, align: StrokeAlign = "center"): number[] {
  return networkToStrokeTriangleChunks(network, strokeWidth, align).flat();
}

export function networkToStrokeTriangleChunks(network: VectorNetwork, strokeWidth: number, align: StrokeAlign = "center"): number[][] {
  const lookup = vectorLookup(network);
  const chainVertices = vectorStrokePolylines(network, lookup)
    .map(polyline => ({
      ...polyline,
      points: roundVectorPolylineCorners(polyline.points, lookup, polyline.closed),
    }))
    .flatMap((polyline, index) => vectorStrokePolylineToTriangleChunks(
      polyline.points,
      lookup,
      strokeWidth,
      polyline.closed,
      polyline.closed ? align : "center",
      `${strokeGeometryCacheKey(strokeWidth, polyline.closed ? align : "center")}:polyline:${index}`,
    ));
  const branchVertices = branchStrokeJoinTriangles(network, strokeWidth, lookup);
  return branchVertices.length > 0 ? [...chainVertices, branchVertices] : chainVertices;
}

function vectorStrokePolylines(network: VectorNetwork, lookup = vectorLookup(network)): Array<{ points: Point[]; closed: boolean }> {
  const degree = new Map<string, number>();
  for (const edge of network.edges) {
    degree.set(edge.from.vertexId, (degree.get(edge.from.vertexId) ?? 0) + 1);
    degree.set(edge.to.vertexId, (degree.get(edge.to.vertexId) ?? 0) + 1);
  }

  const visited = new Set<string>();
  const polylines: Array<{ points: Point[]; closed: boolean }> = [];
  const incident = (vertexId: string) => lookup.incidentEdgesByVertexId.get(vertexId) ?? [];

  const walk = (startEdge: VectorEdge, startVertexId: string): { points: Point[]; closed: boolean } => {
    const sampledEdges: Array<{ edge: VectorEdge; forward: boolean }> = [];
    let currentEdge: VectorEdge | undefined = startEdge;
    let currentVertexId = startVertexId;
    let closed = false;

    while (currentEdge && !visited.has(currentEdge.id)) {
      visited.add(currentEdge.id);
      const forward: boolean = currentEdge.from.vertexId === currentVertexId;
      sampledEdges.push({ edge: currentEdge, forward });
      const nextVertexId: string = forward ? currentEdge.to.vertexId : currentEdge.from.vertexId;
      const candidates: VectorEdge[] = incident(nextVertexId).filter(edge => !visited.has(edge.id));
      if (nextVertexId === startVertexId && candidates.length === 0 && (degree.get(nextVertexId) ?? 0) === 2) {
        closed = true;
        break;
      }
      if ((degree.get(nextVertexId) ?? 0) !== 2 || candidates.length === 0) {
        break;
      }
      currentEdge = candidates[0];
      currentVertexId = nextVertexId;
    }

    const points = sampledEdges.flatMap(({ edge, forward }, index) => {
      const sampled = sampleEdgePointsOriented(lookup.vertexById, edge, adaptiveCurveStepsForEdge(lookup.vertexById, edge), forward);
      return index === 0 ? sampled : sampled.slice(1);
    });
    return { points, closed };
  };

  for (const edge of network.edges) {
    if (visited.has(edge.id)) {
      continue;
    }
    const fromDegree = degree.get(edge.from.vertexId) ?? 0;
    const toDegree = degree.get(edge.to.vertexId) ?? 0;
    if (fromDegree !== 2) {
      polylines.push(walk(edge, edge.from.vertexId));
    } else if (toDegree !== 2) {
      polylines.push(walk(edge, edge.to.vertexId));
    }
  }

  for (const edge of network.edges) {
    if (!visited.has(edge.id)) {
      polylines.push(walk(edge, edge.from.vertexId));
    }
  }

  return polylines.filter(polyline => polyline.points.length >= 2);
}

function sampleEdgePoints(vertices: VectorVertex[], edge: VectorEdge, steps: number): Array<{ x: number; y: number }> {
  return sampleEdgePointsOriented(vertexMap(vertices), edge, steps, true);
}

export function sampleEdgePointsOriented(vertices: Map<string, VectorVertex>, edge: VectorEdge, steps: number, forward: boolean): Array<{ x: number; y: number }> {
  const from = vertices.get(edge.from.vertexId);
  const to = vertices.get(edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  const points = sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, steps);

  return forward ? points : [...points].reverse();
}

function branchStrokeJoinTriangles(network: VectorNetwork, strokeWidth: number, lookup = vectorLookup(network)): number[] {
  const radius = strokeWidth / 2;
  if (radius <= 0) {
    return [];
  }

  const vertices: number[] = [];
  for (const vertex of network.vertices) {
    const incidentEdges = lookup.incidentEdgesByVertexId.get(vertex.id) ?? [];
    if (incidentEdges.length <= 2) {
      continue;
    }

    const rays = incidentEdges
      .flatMap(edge => {
        const ray = branchRay(vertex, edge, lookup.vertexById, radius);
        return ray ? [ray] : [];
      })
      .sort((left, right) => left.angle - right.angle);
    const join = vertex.strokeJoin ?? "miter";
    if (join === "round") {
      appendNumbers(vertices, branchRoundSectorTriangles(vertex, rays));
      continue;
    }

    const contour = rays.flatMap(ray => [ray.left, ray.right]);
    const sorted = removeDuplicatePoints(contour)
      .sort((left, right) => Math.atan2(left.y - vertex.y, left.x - vertex.x) - Math.atan2(right.y - vertex.y, right.x - vertex.x));
    if (sorted.length >= 3) {
      appendNumbers(vertices, triangulateEvenOddContours([sorted]));
    }

    if (join === "miter") {
      appendNumbers(vertices, branchMiterExtensionTriangles(vertex, rays, radius));
    }
  }

  return vertices;
}

function branchRoundSectorTriangles(vertex: VectorVertex, rays: BranchRay[]): number[] {
  const vertices: number[] = [];
  if (rays.length < 2) {
    return vertices;
  }

  for (let index = 0; index < rays.length; index += 1) {
    const current = rays[index]!;
    const next = rays[(index + 1) % rays.length]!;
    const arc = roundJoinArcPoints(vertex, current.left, next.right);
    for (let arcIndex = 0; arcIndex < arc.length - 1; arcIndex += 1) {
      const from = arc[arcIndex]!;
      const to = arc[arcIndex + 1]!;
      vertices.push(vertex.x, vertex.y, from.x, from.y, to.x, to.y);
    }
  }

  return vertices;
}

export interface BranchRay {
  angle: number;
  tangent: Point;
  left: Point;
  right: Point;
}

function branchRay(vertex: VectorVertex, edge: VectorEdge, vertices: VectorVertex[] | Map<string, VectorVertex>, radius: number): BranchRay | undefined {
  const otherVertexId = edge.from.vertexId === vertex.id ? edge.to.vertexId : edge.from.vertexId;
  const other = vertices instanceof Map
    ? vertices.get(otherVertexId)
    : vertices.find(candidate => candidate.id === otherVertexId);
  if (!other) {
    return undefined;
  }

  const tangentTarget = edge.from.vertexId === vertex.id
    ? edge.from.handle ?? other
    : edge.to.handle ?? other;
  const dx = tangentTarget.x - vertex.x;
  const dy = tangentTarget.y - vertex.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.0001) {
    return undefined;
  }

  const tangent = { x: dx / length, y: dy / length };
  const normal = { x: -tangent.y, y: tangent.x };
  return {
    angle: Math.atan2(tangent.y, tangent.x),
    tangent,
    left: { x: vertex.x + normal.x * radius, y: vertex.y + normal.y * radius },
    right: { x: vertex.x - normal.x * radius, y: vertex.y - normal.y * radius },
  };
}

function branchMiterExtensionTriangles(vertex: VectorVertex, rays: BranchRay[], radius: number): number[] {
  const vertices: number[] = [];
  if (rays.length < 2) {
    return vertices;
  }

  const miterLimit = Math.max(1, radius * 4);
  for (let index = 0; index < rays.length; index += 1) {
    const current = rays[index]!;
    const next = rays[(index + 1) % rays.length]!;
    const intersection = lineIntersection(current.left, current.tangent, next.right, next.tangent);
    if (!intersection || Math.hypot(intersection.x - vertex.x, intersection.y - vertex.y) > miterLimit) {
      continue;
    }

    vertices.push(
      current.left.x,
      current.left.y,
      intersection.x,
      intersection.y,
      next.right.x,
      next.right.y,
    );
  }

  return vertices;
}

export function adaptiveCurveStepsForEdge(vertices: VectorVertex[] | Map<string, VectorVertex>, edge: VectorEdge): number {
  const from = vertices instanceof Map
    ? vertices.get(edge.from.vertexId)
    : vertices.find(vertex => vertex.id === edge.from.vertexId);
  const to = vertices instanceof Map
    ? vertices.get(edge.to.vertexId)
    : vertices.find(vertex => vertex.id === edge.to.vertexId);
  if (!from || !to) {
    return 1;
  }

  return adaptiveCurveSteps(from, to, edge.from.handle, edge.to.handle);
}

function distancePointToLine(point: Point, from: Point, to: Point): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.0001) {
    return Math.hypot(point.x - from.x, point.y - from.y);
  }

  return Math.abs((point.x - from.x) * dy - (point.y - from.y) * dx) / length;
}

/**
 * TS fallback: stroke a polyline into triangles.
 * @internal Prefer `strokePolylineTriangles` from meshKernels.
 */
/**
 * TS fallback: stroke a polyline into triangles.
 * @internal Prefer `strokePolylineTriangles` from meshKernels.
 */
export function strokePolylineToTriangles(
  points: Array<{ x: number; y: number }>,
  strokeWidth: number,
  closed: boolean,
  align: StrokeAlign = "inside",
): number[] {
  const cleanPoints = removeDuplicatePoints(closed ? removeClosingPoint(points) : points);
  if (cleanPoints.length < 2 || strokeWidth <= 0) {
    return [];
  }

  if (closed && cleanPoints.length >= 3) {
    const { left, right } = closedStrokeRings(cleanPoints, strokeWidth, align);
    const ringVertices = strokeRingToTriangles(left, right);
    if (ringVertices.length > 0) {
      return ringVertices;
    }

    // Avoid even-odd fill of offset rings — self-intersecting closed paths blob.
    return strokePolylineSegmentTriangleChunks(
      cleanPoints,
      strokeWidth,
      () => "miter",
      "uncached",
      true,
    ).flat();
  }

  const left = offsetPolylineSide(cleanPoints, strokeWidth, false, 1);
  const right = offsetPolylineSide(cleanPoints, strokeWidth, false, -1);
  if (left.length === right.length && left.length === cleanPoints.length) {
    return openStrokeStripTriangles(left, right);
  }

  return strokePolylineSegmentTriangleChunks(cleanPoints, strokeWidth, () => "miter", "uncached", false).flat();
}

function vectorStrokePolylineToTriangles(
  points: Point[],
  network: VectorNetwork,
  strokeWidth: number,
  closed: boolean,
  align: StrokeAlign = "inside",
): number[] {
  return vectorStrokePolylineToTriangleChunks(points, vectorLookup(network), strokeWidth, closed, align, "uncached").flat();
}

function vectorStrokePolylineToTriangleChunks(
  points: Point[],
  lookup: VectorLookup,
  strokeWidth: number,
  closed: boolean,
  align: StrokeAlign = "inside",
  cacheKeyBase: string,
): number[][] {
  const cleanPoints = removeDuplicatePoints(closed ? removeClosingPoint(points) : points);
  if (cleanPoints.length < 2 || strokeWidth <= 0) {
    return [];
  }

  const boundaries = strokeBoundaries(cleanPoints, strokeWidth, closed, align);
  const joinForPoint = vectorJoinForPoint(lookup);

  if (closed && cleanPoints.length >= 3) {
    const first = strokeBoundaryContour(cleanPoints, true, boundaries[0]!, joinForPoint);
    const second = strokeBoundaryContour(cleanPoints, true, boundaries[1]!, joinForPoint);
    // Prefer a local paired strip (same as simple polyline strokes). Even-odd fill of
    // self-intersecting offset rings fills the whole envelope (spirograph blob).
    if (first.length === second.length && first.length >= 3) {
      return [strokeRingToTriangles(first, second)];
    }
    // Unequal contour lengths (e.g. round joins): still emit local segment ribbons.
    return strokePolylineSegmentTriangleChunks(cleanPoints, strokeWidth, joinForPoint, cacheKeyBase, true);
  }

  const first = strokeBoundaryContour(cleanPoints, false, boundaries[0]!, joinForPoint);
  const second = strokeBoundaryContour(cleanPoints, false, boundaries[1]!, joinForPoint);
  if (first.length === second.length && first.length === cleanPoints.length) {
    return openStrokeStripTriangleChunks(first, second, cacheKeyBase);
  }

  return strokePolylineSegmentTriangleChunks(cleanPoints, strokeWidth, joinForPoint, cacheKeyBase, false);
}

function openStrokeStripTriangles(first: Point[], second: Point[]): number[] {
  return openStrokeStripTriangleChunks(first, second, "uncached").flat();
}

function openStrokeStripTriangleChunks(first: Point[], second: Point[], cacheKeyBase: string): number[][] {
  const chunks: number[][] = [];
  for (let startIndex = 0; startIndex < first.length - 1; startIndex += strokeTriangleChunkPointSize) {
    const endIndex = Math.min(first.length - 1, startIndex + strokeTriangleChunkPointSize);
    chunks.push(cachedStrokeTriangleChunk(`${cacheKeyBase}:strip:${pointRangeCacheKey(first, startIndex, endIndex + 1)}:${pointRangeCacheKey(second, startIndex, endIndex + 1)}`, () => {
      const vertices: number[] = [];
      appendOpenStrokeStripTriangles(first, second, startIndex, endIndex, vertices);
      return vertices;
    }));
  }

  return chunks;
}

function appendOpenStrokeStripTriangles(first: Point[], second: Point[], startIndex: number, endIndex: number, vertices: number[]): void {
  for (let index = startIndex; index < endIndex; index += 1) {
    const firstCurrent = first[index]!;
    const firstNext = first[index + 1]!;
    const secondCurrent = second[index]!;
    const secondNext = second[index + 1]!;
    vertices.push(
      firstCurrent.x,
      firstCurrent.y,
      firstNext.x,
      firstNext.y,
      secondCurrent.x,
      secondCurrent.y,
      secondCurrent.x,
      secondCurrent.y,
      firstNext.x,
      firstNext.y,
      secondNext.x,
      secondNext.y,
    );
  }
}

function pointRangeCacheKey(points: Point[], startIndex: number, endIndex: number): string {
  return points.slice(startIndex, endIndex)
    .map(point => `${formatCacheNumber(point.x)},${formatCacheNumber(point.y)}`)
    .join(";");
}

function strokePolylineSegmentTriangleChunks(
  points: Point[],
  strokeWidth: number,
  joinForPoint: (point: Point) => VectorStrokeJoin,
  cacheKeyBase: string,
  closed: boolean,
): number[][] {
  const radius = strokeWidth / 2;
  if (points.length < 2 || radius <= 0) {
    return [];
  }

  const segments = indexedPolylineSegments(points, closed);
  const previousByPoint = new Map<number, IndexedSegment>();
  const nextByPoint = new Map<number, IndexedSegment>();
  const chunks: number[][] = [];

  for (let startIndex = 0; startIndex < segments.length; startIndex += strokeTriangleChunkPointSize) {
    const endIndex = Math.min(segments.length, startIndex + strokeTriangleChunkPointSize);
    chunks.push(cachedStrokeTriangleChunk(`${cacheKeyBase}:segments:${closed ? "closed:" : ""}${segmentRangeCacheKey(points, segments, startIndex, endIndex)}`, () => {
      const vertices: number[] = [];
      for (let index = startIndex; index < endIndex; index += 1) {
        const segment = segments[index]!;
        const from = points[segment.fromIndex]!;
        const to = points[segment.toIndex]!;
        vertices.push(...strokeSegmentTriangles(from, to, segment.normal, radius));
      }
      return vertices;
    }));
  }

  for (const segment of segments) {
    previousByPoint.set(segment.toIndex, segment);
    nextByPoint.set(segment.fromIndex, segment);
  }

  const joinStart = closed ? 0 : 1;
  const joinEnd = closed ? points.length : points.length - 1;
  for (let startIndex = joinStart; startIndex < joinEnd; startIndex += strokeTriangleChunkPointSize) {
    const endIndex = Math.min(joinEnd, startIndex + strokeTriangleChunkPointSize);
    chunks.push(cachedStrokeTriangleChunk(`${cacheKeyBase}:joins:${closed ? "closed:" : ""}${pointRangeCacheKey(points, Math.max(0, startIndex - 1), Math.min(points.length, endIndex + 1))}`, () => {
      const vertices: number[] = [];
      for (let pointIndex = startIndex; pointIndex < endIndex; pointIndex += 1) {
        const point = points[pointIndex]!;
        const previous = previousByPoint.get(pointIndex);
        const next = nextByPoint.get(pointIndex);
        if (!previous || !next) {
          continue;
        }

        vertices.push(...openStrokeJoinTriangles(point, previous, next, radius, joinForPoint(point)));
      }
      return vertices;
    }));
  }

  return chunks;
}

function segmentRangeCacheKey(points: Point[], segments: IndexedSegment[], startIndex: number, endIndex: number): string {
  const ids = new Set<number>();
  for (let index = startIndex; index < endIndex; index += 1) {
    const segment = segments[index]!;
    ids.add(segment.fromIndex);
    ids.add(segment.toIndex);
  }

  return [...ids]
    .sort((left, right) => left - right)
    .map(index => {
      const point = points[index]!;
      return `${index}:${formatCacheNumber(point.x)},${formatCacheNumber(point.y)}`;
    })
    .join(";");
}

function strokeSegmentTriangles(from: Point, to: Point, normal: Point, radius: number): number[] {
  const fromLeft = offsetPoint(from, normal, 1, radius);
  const fromRight = offsetPoint(from, normal, -1, radius);
  const toLeft = offsetPoint(to, normal, 1, radius);
  const toRight = offsetPoint(to, normal, -1, radius);
  return [
    fromLeft.x,
    fromLeft.y,
    toLeft.x,
    toLeft.y,
    fromRight.x,
    fromRight.y,
    fromRight.x,
    fromRight.y,
    toLeft.x,
    toLeft.y,
    toRight.x,
    toRight.y,
  ];
}

function openStrokeJoinTriangles(
  point: Point,
  previous: IndexedSegment,
  next: IndexedSegment,
  radius: number,
  join: VectorStrokeJoin,
): number[] {
  const previousLeft = offsetPoint(point, previous.normal, 1, radius);
  const nextLeft = offsetPoint(point, next.normal, 1, radius);
  const previousRight = offsetPoint(point, previous.normal, -1, radius);
  const nextRight = offsetPoint(point, next.normal, -1, radius);
  return [
    ...openStrokeSideJoinTriangles(point, previousLeft, nextLeft, previous, next, radius, join),
    ...openStrokeSideJoinTriangles(point, previousRight, nextRight, previous, next, radius, join),
  ];
}

function openStrokeSideJoinTriangles(
  point: Point,
  from: Point,
  to: Point,
  previous: IndexedSegment,
  next: IndexedSegment,
  radius: number,
  join: VectorStrokeJoin,
): number[] {
  if (Math.hypot(from.x - to.x, from.y - to.y) <= 0.001) {
    return [];
  }

  if (join === "round") {
    const arc = roundJoinArcPoints(point, from, to);
    const vertices: number[] = [];
    for (let index = 0; index < arc.length - 1; index += 1) {
      const current = arc[index]!;
      const nextPoint = arc[index + 1]!;
      vertices.push(point.x, point.y, current.x, current.y, nextPoint.x, nextPoint.y);
    }
    return vertices;
  }

  if (join === "miter") {
    const miter = miterJoinPoints(point, from, to, previous.tangent, next.tangent, Math.max(1, radius * 4));
    if (miter.length === 1) {
      const tip = miter[0]!;
      return [from.x, from.y, tip.x, tip.y, to.x, to.y];
    }
    if (miter.length === 2) {
      const a = miter[0]!;
      const b = miter[1]!;
      return [
        from.x, from.y, a.x, a.y, b.x, b.y,
        from.x, from.y, b.x, b.y, to.x, to.y,
      ];
    }
  }

  return [point.x, point.y, from.x, from.y, to.x, to.y];
}

function strokeBoundaries(points: Point[], strokeWidth: number, closed: boolean, align: StrokeAlign): [StrokeBoundary, StrokeBoundary] {
  if (!closed || align === "center") {
    return [
      { side: 1, distance: strokeWidth / 2 },
      { side: -1, distance: strokeWidth / 2 },
    ];
  }

  const winding = signedArea(points) >= 0 ? 1 : -1;
  const insideSide = winding as 1 | -1;
  const outsideSide = -winding as 1 | -1;
  return align === "outside"
    ? [{ side: 0, distance: 0 }, { side: outsideSide, distance: strokeWidth }]
    : [{ side: 0, distance: 0 }, { side: insideSide, distance: strokeWidth }];
}

export interface StrokeBoundary {
  side: -1 | 0 | 1;
  distance: number;
}

export interface IndexedSegment {
  fromIndex: number;
  toIndex: number;
  tangent: Point;
  normal: Point;
}

function indexedPolylineSegments(points: Point[], closed: boolean): IndexedSegment[] {
  const count = closed ? points.length : points.length - 1;
  const segments: IndexedSegment[] = [];
  for (let index = 0; index < count; index += 1) {
    const from = points[index]!;
    const toIndex = (index + 1) % points.length;
    const to = points[toIndex]!;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0.0001) {
      continue;
    }

    const tangent = { x: dx / length, y: dy / length };
    segments.push({
      fromIndex: index,
      toIndex,
      tangent,
      normal: { x: -tangent.y, y: tangent.x },
    });
  }

  return segments;
}

function strokeBoundaryContour(
  points: Point[],
  closed: boolean,
  boundary: StrokeBoundary,
  joinForPoint: (point: Point) => VectorStrokeJoin,
): Point[] {
  if (boundary.side === 0 || boundary.distance <= 0) {
    return points;
  }

  const segments = indexedPolylineSegments(points, closed);
  if (segments.length === 0) {
    return [];
  }

  const contour: Point[] = [];
  for (let pointIndex = 0; pointIndex < points.length; pointIndex += 1) {
    const point = points[pointIndex]!;
    if (!closed && pointIndex === 0) {
      contour.push(offsetPoint(point, segments[0]!.normal, boundary.side, boundary.distance));
      continue;
    }
    if (!closed && pointIndex === points.length - 1) {
      contour.push(offsetPoint(point, segments.at(-1)!.normal, boundary.side, boundary.distance));
      continue;
    }

    const previous = segments.find(segment => segment.toIndex === pointIndex);
    const next = segments.find(segment => segment.fromIndex === pointIndex);
    if (!previous || !next) {
      continue;
    }

    contour.push(...joinBoundaryPoints(point, previous, next, boundary, joinForPoint(point)));
  }

  return removeDuplicatePoints(contour);
}

function joinBoundaryPoints(
  point: Point,
  previous: IndexedSegment,
  next: IndexedSegment,
  boundary: StrokeBoundary,
  join: VectorStrokeJoin,
): Point[] {
  const previousPoint = offsetStrokeBoundaryPoint(point, previous.normal, boundary);
  const nextPoint = offsetStrokeBoundaryPoint(point, next.normal, boundary);
  if (Math.hypot(previousPoint.x - nextPoint.x, previousPoint.y - nextPoint.y) <= 0.001) {
    return [previousPoint];
  }

  if (join === "round") {
    return roundJoinArcPoints(point, previousPoint, nextPoint);
  }

  if (join === "miter") {
    return miterJoinPoints(
      point,
      previousPoint,
      nextPoint,
      previous.tangent,
      next.tangent,
      Math.max(1, boundary.distance * 4),
    );
  }

  return [previousPoint, nextPoint];
}

/**
 * Returns a single miter tip when within the limit; otherwise **miter-clips**
 * to two points that stay on the offset rays (SVG-style clip).
 *
 * A single bisector tip past the limit does **not** lie on either offset line,
 * so index-paired strip chords undershoot the true parallel and the whole
 * adjacent edge looks thinner than `strokeWidth`.
 *
 * Falling back to a plain bevel (`[previousPoint, nextPoint]`) is unsafe for
 * inside/outside-aligned offset contours: at acute corners those bevel points
 * can leave the shape and even-odd triangulation then fills the interior.
 */
function miterJoinPoints(
  point: Point,
  previousPoint: Point,
  nextPoint: Point,
  previousTangent: Point,
  nextTangent: Point,
  miterLimit: number,
): Point[] {
  const intersection = lineIntersection(previousPoint, previousTangent, nextPoint, nextTangent);
  if (!intersection) {
    return [previousPoint, nextPoint];
  }

  const dx = intersection.x - point.x;
  const dy = intersection.y - point.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= 0.0001) {
    return [previousPoint, nextPoint];
  }
  if (distance <= miterLimit) {
    return [intersection];
  }

  const bx = dx / distance;
  const by = dy / distance;
  const clipPrevious = rayHitMiterClipPlane(previousPoint, previousTangent, point, bx, by, miterLimit);
  const clipNext = rayHitMiterClipPlane(nextPoint, nextTangent, point, bx, by, miterLimit);
  if (!clipPrevious || !clipNext) {
    return [previousPoint, nextPoint];
  }
  return [clipPrevious, clipNext];
}

/** Intersect `origin + t * direction` with plane `(X - vertex) · bisector = miterLimit`. */
function rayHitMiterClipPlane(
  origin: Point,
  direction: Point,
  vertex: Point,
  bisectorX: number,
  bisectorY: number,
  miterLimit: number,
): Point | undefined {
  const denom = direction.x * bisectorX + direction.y * bisectorY;
  if (Math.abs(denom) <= 0.000001) {
    return undefined;
  }
  const t = (miterLimit - ((origin.x - vertex.x) * bisectorX + (origin.y - vertex.y) * bisectorY)) / denom;
  if (t < -0.0001) {
    return undefined;
  }
  return {
    x: origin.x + direction.x * t,
    y: origin.y + direction.y * t,
  };
}

function offsetStrokeBoundaryPoint(point: Point, normal: Point, boundary: StrokeBoundary): Point {
  if (boundary.side === 0) {
    return point;
  }

  return offsetPoint(point, normal, boundary.side, boundary.distance);
}

function roundJoinArcPoints(center: Point, from: Point, to: Point): Point[] {
  const radius = Math.hypot(from.x - center.x, from.y - center.y);
  if (radius <= 0.0001) {
    return [from, to];
  }

  const start = Math.atan2(from.y - center.y, from.x - center.x);
  let delta = Math.atan2(to.y - center.y, to.x - center.x) - start;
  while (delta <= -Math.PI) {
    delta += Math.PI * 2;
  }
  while (delta > Math.PI) {
    delta -= Math.PI * 2;
  }

  const segments = arcSegmentCount(radius, Math.abs(delta));
  return Array.from({ length: segments + 1 }, (_, index) => {
    const angle = start + (delta * index) / segments;
    return {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  });
}

function arcSegmentCount(radius: number, angle: number): number {
  if (radius <= vectorCurveTolerance || angle <= 0.0001) {
    return 1;
  }

  const maxAngle = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - vectorCurveTolerance / radius)));
  return Math.min(256, Math.max(12, Math.ceil(angle / Math.max(0.001, maxAngle))));
}

function closedStrokeRings(points: Point[], strokeWidth: number, align: StrokeAlign): { left: Point[]; right: Point[] } {
  const winding = signedArea(points) >= 0 ? 1 : -1;
  const insideSide = winding as 1 | -1;
  const outsideSide = -winding as 1 | -1;

  if (align === "center") {
    return {
      left: offsetPolylineSide(points, strokeWidth, true, 1),
      right: offsetPolylineSide(points, strokeWidth, true, -1),
    };
  }

  if (align === "outside") {
    return {
      left: points,
      right: offsetPolylineSide(points, strokeWidth * 2, true, outsideSide),
    };
  }

  return {
    left: points,
    right: offsetPolylineSide(points, strokeWidth * 2, true, insideSide),
  };
}

function strokeRingToTriangles(left: Point[], right: Point[]): number[] {
  if (left.length < 3 || left.length !== right.length) {
    return [];
  }

  const vertices: number[] = [];
  for (let index = 0; index < left.length; index += 1) {
    const nextIndex = (index + 1) % left.length;
    const leftCurrent = left[index]!;
    const leftNext = left[nextIndex]!;
    const rightCurrent = right[index]!;
    const rightNext = right[nextIndex]!;
    vertices.push(
      leftCurrent.x,
      leftCurrent.y,
      leftNext.x,
      leftNext.y,
      rightCurrent.x,
      rightCurrent.y,
      rightCurrent.x,
      rightCurrent.y,
      leftNext.x,
      leftNext.y,
      rightNext.x,
      rightNext.y,
    );
  }

  return vertices;
}

function offsetPolylineSide(points: Point[], strokeWidth: number, closed: boolean, side: 1 | -1): Point[] {
  const halfWidth = strokeWidth / 2;
  const segments = polylineSegments(points, closed);
  if (segments.length === 0) {
    return [];
  }

  const result: Point[] = [];
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index]!;
    if (!closed && index === 0) {
      result.push(offsetPoint(point, segments[0]!.normal, side, halfWidth));
      continue;
    }
    if (!closed && index === points.length - 1) {
      result.push(offsetPoint(point, segments.at(-1)!.normal, side, halfWidth));
      continue;
    }

    const previous = segments[(index - 1 + segments.length) % segments.length]!;
    const next = segments[index % segments.length]!;
    result.push(...joinOffsetPoints(point, previous, next, side, halfWidth));
  }

  return removeDuplicatePoints(result);
}

function polylineSegments(points: Point[], closed: boolean): Array<{ tangent: Point; normal: Point }> {
  const count = closed ? points.length : points.length - 1;
  const segments: Array<{ tangent: Point; normal: Point }> = [];
  for (let index = 0; index < count; index += 1) {
    const from = points[index]!;
    const to = points[(index + 1) % points.length]!;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0.0001) {
      continue;
    }

    const tangent = { x: dx / length, y: dy / length };
    segments.push({
      tangent,
      normal: { x: -tangent.y, y: tangent.x },
    });
  }

  return segments;
}

function joinOffsetPoints(
  point: Point,
  previous: { tangent: Point; normal: Point },
  next: { tangent: Point; normal: Point },
  side: 1 | -1,
  halfWidth: number,
): Point[] {
  const previousPoint = offsetPoint(point, previous.normal, side, halfWidth);
  const nextPoint = offsetPoint(point, next.normal, side, halfWidth);
  return miterJoinPoints(
    point,
    previousPoint,
    nextPoint,
    previous.tangent,
    next.tangent,
    Math.max(1, halfWidth * 4),
  );
}

function offsetPoint(point: Point, normal: Point, side: 1 | -1, distance: number): Point {
  return {
    x: point.x + normal.x * side * distance,
    y: point.y + normal.y * side * distance,
  };
}

function lineIntersection(pointA: Point, directionA: Point, pointB: Point, directionB: Point): Point | undefined {
  const cross = directionA.x * directionB.y - directionA.y * directionB.x;
  if (Math.abs(cross) <= 0.000001) {
    return undefined;
  }

  const dx = pointB.x - pointA.x;
  const dy = pointB.y - pointA.y;
  const t = (dx * directionB.y - dy * directionB.x) / cross;
  return {
    x: pointA.x + directionA.x * t,
    y: pointA.y + directionA.y * t,
  };
}

function removeClosingPoint(points: Array<{ x: number; y: number }>): Point[] {
  const cleanPoints = removeDuplicatePoints(points);
  const first = cleanPoints[0];
  const last = cleanPoints.at(-1);
  if (first && last && Math.hypot(first.x - last.x, first.y - last.y) <= 0.001) {
    return cleanPoints.slice(0, -1);
  }

  return cleanPoints;
}

export function roundVectorPolylineCorners(points: Point[], lookup: VectorLookup, closed: boolean): Point[] {
  const cleanPoints = removeDuplicatePoints(closed ? removeClosingPoint(points) : points);
  if (cleanPoints.length < 3) {
    return cleanPoints;
  }

  const rounded: Point[] = [];
  for (let index = 0; index < cleanPoints.length; index += 1) {
    const point = cleanPoints[index]!;
    const vertex = vectorVertexAtPoint(lookup, point);
    const radius = vertex?.cornerRadius ?? 0;
    const previous = index === 0 ? closed ? cleanPoints.at(-1) : undefined : cleanPoints[index - 1];
    const next = index === cleanPoints.length - 1 ? closed ? cleanPoints[0] : undefined : cleanPoints[index + 1];
    if (!previous || !next || radius <= 0) {
      rounded.push(point);
      continue;
    }

    const previousDistance = Math.hypot(previous.x - point.x, previous.y - point.y);
    const nextDistance = Math.hypot(next.x - point.x, next.y - point.y);
    const distance = Math.min(radius, previousDistance / 2, nextDistance / 2);
    if (distance <= 0.001) {
      rounded.push(point);
      continue;
    }

    const entry = pointAlong(point, previous, distance);
    const exit = pointAlong(point, next, distance);
    rounded.push(entry, ...quadraticCornerPoints(entry, point, exit).slice(1));
  }

  return removeDuplicatePoints(rounded);
}

function vectorJoinForPoint(lookup: VectorLookup): (point: Point) => VectorStrokeJoin {
  return point => vectorVertexAtPoint(lookup, point)?.strokeJoin ?? "miter";
}

function vectorVertexAtPoint(lookup: VectorLookup, point: Point): VectorVertex | undefined {
  for (const key of nearbyPointLookupKeys(point)) {
    const vertex = lookup.vertexByPointKey.get(key);
    if (vertex && Math.hypot(vertex.x - point.x, vertex.y - point.y) <= 0.0015) {
      return vertex;
    }
  }

  return undefined;
}

function pointAlong(from: Point, to: Point, distance: number): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.0001) {
    return { ...from };
  }

  return {
    x: from.x + (dx / length) * distance,
    y: from.y + (dy / length) * distance,
  };
}

function quadraticCornerPoints(from: Point, control: Point, to: Point): Point[] {
  const steps = quadraticSegmentCount(from, control, to);
  return Array.from({ length: steps + 1 }, (_, index) => {
    const t = index / steps;
    const mt = 1 - t;
    return {
      x: mt * mt * from.x + 2 * mt * t * control.x + t * t * to.x,
      y: mt * mt * from.y + 2 * mt * t * control.y + t * t * to.y,
    };
  });
}

function quadraticSegmentCount(from: Point, control: Point, to: Point): number {
  const flatness = distancePointToLine(control, from, to);
  if (flatness <= vectorCurveTolerance) {
    return 1;
  }

  return Math.min(256, Math.max(12, Math.ceil(Math.sqrt(flatness / vectorCurveTolerance))));
}
